import React from 'react';
import PropTypes from 'prop-types';
import {ButtonWithIcon, CardSkeletonLoader} from '@/components/atoms';
import {Accordion, AccordionItem} from '@/components/molecules';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {ROUTES} from '@/utils/constants/routesConstants';
import {formatActivityTypeForReview} from '@/helpers/activityUtils';

const REVIEW_SPACING = {
  accordionGapClass: 'space-y-5',
  threeColGridClass: 'grid-cols-3 gap-6'
};

const formatDateYYYYMMDD = (value, getFormattedDate) =>
  getFormattedDate(value, false, '-', 'yyyy-mm-dd');

const formatReviewValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

const EasyOrderReviewGridRow = ({
  items,
  dataTestId,
  preserveRhythm = false
}) => (
  <div
    className={`mt-3 grid ${REVIEW_SPACING.threeColGridClass}`}
    data-testid={dataTestId}>
    {items.map((item, idx) =>
      item?.isSpacer ? (
        <div
          key={`spacer-${idx}`}
          className='invisible pb-2'
          aria-hidden='true'
          data-testid={`${dataTestId}-spacer`}
        />
      ) : (
        <div key={item.label} className='pb-2'>
          <p className='pl-1 text-sm text-[#D0D0D0]'>{item.label}</p>
          <p className='pl-1 font-bold text-white'>{item.value}</p>
        </div>
      )
    )}
    {preserveRhythm &&
      items.length < 3 &&
      Array.from({length: 3 - items.length}).map((_, idx) => (
        <div
          key={`auto-spacer-${idx}`}
          className='invisible pb-2'
          aria-hidden='true'
          data-testid={`${dataTestId}-auto-spacer`}
        />
      ))}
  </div>
);

EasyOrderReviewGridRow.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.node,
      isSpacer: PropTypes.bool
    })
  ).isRequired,
  dataTestId: PropTypes.string,
  preserveRhythm: PropTypes.bool
};

const EasyOrderSectionTitle = ({label, className = ''}) => (
  <div className={className}>
    <h3 className='text-base font-bold text-white'>{label}</h3>
    <div
      className='mt-2 border-b border-[color:var(--color-border-divider)]'
      data-testid={`easy-order-${label.toLowerCase().replace(/\s+/g, '-')}-divider`}
    />
  </div>
);

EasyOrderSectionTitle.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string
};

const EasyOrderActivityReview = ({
  loading,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  isEditable,
  editButtonSchema,
  navigate,
  activityFormData,
  getFormattedDate
}) => (
  <div
    data-testid='easy-order-review'
    className='easy-order-review-container w-full'>
    {loading && <CardSkeletonLoader count={3} />}
    {!loading && (
      <>
        {showToast && (
          <DuplicateAlert
            type='info'
            message={toastMessage}
            show={true}
            onCloseClick={handleToastClose}
            handleClick={handleViewActivity}
            buttonText='View Activity'
          />
        )}
        <div className='sales-target-review mt-2 w-full'>
          <Accordion
            isDark
            size='large'
            content={
              <div className={`${REVIEW_SPACING.accordionGapClass} w-full`}>
                <AccordionItem
                  isDark
                  expanded
                  label='Setup'
                  rightAccessoryChild={
                    isEditable ? (
                      <ButtonWithIcon
                        uiSchema={{
                          ...editButtonSchema
                        }}
                        iconLeading='edit'
                        handleButton={() => navigate(ROUTES.ACTIVITY_ADD)}
                      />
                    ) : null
                  }
                  content={
                    <div className='w-full'>
                      <EasyOrderSectionTitle
                        label='Activity Setup'
                        className='mb-3'
                      />
                      <EasyOrderReviewGridRow
                        dataTestId='easy-order-setup-grid-row'
                        preserveRhythm
                        items={[
                          {
                            label: 'Activity Type',
                            value: formatActivityTypeForReview(
                              activityFormData?.activityType
                            )
                          },
                          {
                            label: 'Points for Successful Upload',
                            value: formatReviewValue(
                              activityFormData?.pointsForSuccessfulUpload
                            )
                          },
                          {
                            label: 'Points per Line Item',
                            value: formatReviewValue(
                              activityFormData?.pointsPerLineItem
                            )
                          },
                          {
                            label: 'Photo Order Modal Display Limit',
                            value: formatReviewValue(
                              activityFormData?.photoOrderModalDisplayLimit
                            )
                          }
                        ]}
                      />

                      <EasyOrderSectionTitle
                        label='Availability'
                        className='mb-3 mt-6'
                      />
                      <EasyOrderReviewGridRow
                        dataTestId='easy-order-availability-grid-row'
                        items={[
                          {
                            label: 'Active Start Date',
                            value: formatDateYYYYMMDD(
                              activityFormData?.startDate,
                              getFormattedDate
                            )
                          },
                          {
                            label: 'Has End Date',
                            value: activityFormData?.hasEndDate ? 'YES' : 'NO'
                          },
                          {
                            label: 'Active End Date',
                            value: activityFormData?.hasEndDate
                              ? formatDateYYYYMMDD(
                                  activityFormData?.endDate,
                                  getFormattedDate
                                )
                              : '-'
                          }
                        ]}
                      />
                    </div>
                  }
                />
              </div>
            }
          />
        </div>
      </>
    )}
  </div>
);

EasyOrderActivityReview.propTypes = {
  loading: PropTypes.bool.isRequired,
  showToast: PropTypes.bool.isRequired,
  toastMessage: PropTypes.string,
  handleToastClose: PropTypes.func.isRequired,
  handleViewActivity: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  editButtonSchema: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  activityFormData: PropTypes.object.isRequired,
  getFormattedDate: PropTypes.func.isRequired
};

export default React.memo(EasyOrderActivityReview);
